import isNull from 'lodash/isNull'
import { distinctUntilChanged, map, takeWhile } from 'rxjs'

import { PathTool } from '../path/Path.tool'
import { convertAbsoluteDistanceToFormattedString } from '../../../../../../components/markup/utils/helpers'
import { AzureFeatureFlag } from '../../../../../../models/azureFeatureFlags'
import { AzureFeatureFlagIds } from '../../../../../../shared/constants/azure-feature-flags'
import { DRAWING_TYPES } from '../../../../../../shared/constants/drawable-types'
import { isAzureFeatureFlagEnabled } from '../../../../../../shared/services/azure-feature-flag-services/azure-feature-flag-services'
import {
    applyScaleFactorToPathArea,
    applyScaleFactorToPathLength,
} from '../../../../../../utils/calculations/scaleConversion/scaleConversion'
import { initialToolsState, ToolsState } from '../../../../../slices/tools'
import {
    AnalyticsActionEdgeType,
    AnalyticsActionType,
    Cursors,
    IMUPState,
    PaperToolConfig,
    VIEW_MODE,
} from '../../../../../types'
import { SNAPPING_TOLERANCE } from '../../../../../utils/constants'
import { getElementsWithinDistance, getSnapPoint, getSnapPointOnDraw } from '../../../../utils/Snapping'

const DIMENSION_LABEL_FONT_SIZE = 12
const DIMENSION_LABEL_Y_OFFSET = 6
const DIMENSION_LABEL_PADDING = 4

/**
 * Rectangle.tool.tsx
 * Creates a corner to corner rectangle upon mouse drag
 */
export class Rectangle extends PathTool {
    static NAME = 'RECTANGLE'
    static CURSOR = Cursors.CROSSHAIR

    protected startingPoint: paper.Point | null = null

    protected color: ToolsState['color'] = initialToolsState.color
    protected strokeWidth = 0 // set to zero for the stroke width of the created rectangle
    protected opacity: ToolsState['areaOpacityValue'] = initialToolsState.areaOpacityValue
    protected currentPaperId: number | null = null
    protected snappingEnabled = false
    protected itemsToIgnoreHitFilter: paper.Item[] | null = null

    private dimensionLabel: paper.Group | null = null
    private azureFeatureFlags: AzureFeatureFlag[] | null = null

    constructor(config: PaperToolConfig) {
        super(config)
        this.name = Rectangle.NAME
        this.cursor = Rectangle.CURSOR

        this.mediator
            .get$()
            .pipe(
                takeWhile(({ common: { activeMode } }: IMUPState) => activeMode === VIEW_MODE.Markup2D),
                map(({ tools: { areaOpacityValue, snappingEnabled }, flags: { azureFeatureFlags } }: IMUPState) => ({
                    areaOpacityValue,
                    snappingEnabled,
                    azureFeatureFlags,
                })),
                distinctUntilChanged()
            )
            .subscribe(({ areaOpacityValue, snappingEnabled, azureFeatureFlags }) => {
                this.opacity = areaOpacityValue
                this.snappingEnabled = snappingEnabled
                this.azureFeatureFlags = azureFeatureFlags
            })

        this.mediator
            .get$()
            .pipe(
                takeWhile(({ common: { activeMode } }: IMUPState) => activeMode === VIEW_MODE.Markup2D),
                distinctUntilChanged((prev, curr) => prev['2D'].currentZoom === curr['2D'].currentZoom)
            )
            .subscribe(() => {
                if (this.currentPaperId === null) return

                const rect = this.paper.project.getItem({ id: this.currentPaperId }) as paper.Path.Rectangle

                if (rect) this.updateDimensionLabel(rect)
            })
    }

    private getSnappingPoint(eventPoint: paper.Point): paper.Point {
        if (this.snappingEnabled && this.startingPoint) {
            const snappingItems = this.getSnappingItems()

            const snapPoint = getSnapPoint(snappingItems, eventPoint)

            // ignore snapping if the start point equal calculated snap point
            return snapPoint.x !== this.startingPoint.x || snapPoint.y !== this.startingPoint.y ? snapPoint : eventPoint
        }

        return eventPoint
    }

    private removeDimensionLabel = (): void => {
        this.dimensionLabel?.remove()
        this.dimensionLabel = null
    }

    private updateDimensionLabel = (rect: paper.Path.Rectangle): void => {
        if (!isAzureFeatureFlagEnabled(this.azureFeatureFlags, AzureFeatureFlagIds.plenary_rimboard)) {
            return
        }

        const chunk = this.getActiveDocumentChunk()
        const scaleArgs = {
            scaleFactor: this.scaleFactor,
            dpi: chunk?.dpi ?? null,
            xCalibrationFactor: chunk?.calibration_factor_x ?? 1,
            yCalibrationFactor: chunk?.calibration_factor_y ?? 1,
            pdfScale: chunk?.pdf_scale ?? 1,
        }

        const widthFeet = applyScaleFactorToPathLength({
            ...scaleArgs,
            pxValue: rect.bounds.width,
            coordinates: [
                [0, 0],
                [1, 0],
            ],
        })
        const heightFeet = applyScaleFactorToPathLength({
            ...scaleArgs,
            pxValue: rect.bounds.height,
            coordinates: [
                [0, 0],
                [0, 1],
            ],
        })

        const multiplicationSign = '\u00d7'
        const labelContent = `${convertAbsoluteDistanceToFormattedString(
            widthFeet
        )} ${multiplicationSign} ${convertAbsoluteDistanceToFormattedString(heightFeet)}`
        const zoom = this.paper.view.zoom
        const fontSize = DIMENSION_LABEL_FONT_SIZE / zoom
        const yOffset = (DIMENSION_LABEL_FONT_SIZE + DIMENSION_LABEL_Y_OFFSET) / zoom
        const labelPos = rect.bounds.topCenter.add(new this.paper.Point(0, -yOffset))

        this.removeDimensionLabel()

        const textItem = new this.paper.PointText(labelPos)

        textItem.content = labelContent
        textItem.fontSize = fontSize
        textItem.justification = 'center'
        textItem.fillColor = new this.paper.Color('white')

        const padding = DIMENSION_LABEL_PADDING / zoom
        const textBounds = textItem.bounds
        const bg = new this.paper.Path.Rectangle({
            x: textBounds.x - padding,
            y: textBounds.y - padding,
            width: textBounds.width + padding * 2,
            height: textBounds.height + padding * 2,
        })

        bg.fillColor = new this.paper.Color('black')
        bg.strokeWidth = 0

        this.dimensionLabel = new this.paper.Group([bg, textItem])
        this.dimensionLabel.locked = true
    }

    protected draw(color: paper.Color | null, from: paper.Point, to: paper.Point): paper.Path.Rectangle {
        const path = new this.paper.Path.Rectangle(from, to)

        path.opacity = this.opacity
        path.fillColor = color
        path.data.shapeType = DRAWING_TYPES.AREA

        this.currentPaperId = path.id

        return path
    }

    /**
     * Clear the points that are being drawn with the tool
     */
    cancel = () => {
        const rect = this.paper.project.getItem({ id: this.currentPaperId })

        if (rect) {
            rect.remove()
            this.mediator.mediate('2D', {
                latestAnalyticsAction: {
                    startOrStop: AnalyticsActionEdgeType.STOP,
                    actionType: AnalyticsActionType.CREATE,
                },
            })
        }

        this.startingPoint = null
        this.removeDimensionLabel()

        // remove ignore hit filter when we cancel to be able to snap to elements
        if (!isNull(this.itemsToIgnoreHitFilter)) {
            this.itemsToIgnoreHitFilter.forEach((item) => (item.data.ignoreHitFilter = false))
        }

        this.itemsToIgnoreHitFilter = null

        this.setState('common', { cursor: this.cursor, tooltip: { title: '', visible: false, color: '#000000' } })
    }

    onMouseDown = (event: paper.ToolEvent): void => {
        if (this.isPanningClick(event)) return

        if (this.startingPoint) {
            this.startingPoint = null
            this.removeDimensionLabel()

            if (this.currentPaperId) {
                this.mediator.mediate('2D', {
                    latestAnalyticsAction: {
                        startOrStop: AnalyticsActionEdgeType.STOP,
                        actionType: AnalyticsActionType.CREATE,
                    },
                })

                this.setState('2D', { drawablesToCreate: [this.currentPaperId] })
            }

            this.currentPaperId = null
            // The shape has been created at this point, tool state has been reset
        } else {
            this.startingPoint = event.point
            this.setSnappingItems()

            this.mediator.mediate('2D', {
                latestAnalyticsAction: {
                    startOrStop: AnalyticsActionEdgeType.START,
                    actionType: AnalyticsActionType.CREATE,
                },
            })

            if (this.snappingEnabled) {
                const nearElements = getElementsWithinDistance(this.paper, event.point, SNAPPING_TOLERANCE)

                // ignore hit filter because we will automatically snap
                nearElements.forEach((element) => (element.data.ignoreHitFilter = true))

                // set items to ignore hit filter to remove it on cancel
                this.itemsToIgnoreHitFilter = nearElements

                if (!!nearElements.length) {
                    this.startingPoint = getSnapPointOnDraw(this.paper, event.point, nearElements as paper.Path[])
                }
            }

            this.setScaleFromPointClick(event.point)

            // Begin to draw the shape
            this.draw(new this.paper.Color(this.color), this.startingPoint, this.startingPoint)
        }
    }

    onMouseUp = () => {
        this.setState('common', { cursor: this.cursor, tooltip: { title: '', visible: false, color: '#000000' } })
    }

    onMouseDrag = (event) => {
        this.toolPanning(event)
    }

    onMouseMove = (event: paper.MouseEvent): void => {
        if (this.startingPoint instanceof this.paper.Point) {
            // remove the previous rectangle by ID (not lastChild, since the dimension label may be the last item)
            this.paper.project.getItem({ id: this.currentPaperId })?.remove()

            const eventPoint = this.getSnappingPoint(event.point)

            const rect = this.draw(new this.paper.Color(this.color), this.startingPoint, eventPoint)

            this.updateDimensionLabel(rect)

            this.mediator.mediate('common', {
                tooltip: {
                    title: `Area: ${applyScaleFactorToPathArea({
                        pxValue: rect.area,
                        scaleFactor: this.scaleFactor,
                        dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                        xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                        yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,

                        pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                    }).toFixed(2)} sqft`,
                    visible: true,
                    color: '#000000',
                },
            })
        }
    }
}
